import { DateTime } from "luxon";
import { Icon, IconButton, Panel, Timeline } from "rsuite";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import tinycolor from "tinycolor2";
import { TranslationContext } from "../../umbilical_tools";
import jssVariables from "../../jss_setup";

const priceInfoDataStyles = createUseStyles({
  timeline: {
    "marginLeft": "20px",
    "& .rs-timeline-item-tail": {
      left: "12px !important",
      background: (props) =>
        tinycolor(props.baseColor).lighten(50).toHexString(),
    },
    "& .rs-timeline-item-custom-dot": {
      "& .rs-icon": {
        position: "absolute",
        background: "#fff",
        top: 0,
        left: "-2px",
        border: (props) => `2px solid ${props.baseColor}`,
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        fontSize: "18px",
        paddingTop: "5px",
        color: (props) => props.baseColor,
        marginLeft: "0px",
      },
    },
    "& .rs-timeline-item-content": {
      marginLeft: "24px",
    },
  },
  price: {
    color: (props) => props.baseColor,
  },
  normalText: { fontSize: "large" },
  capitalize: {
    textTransform: "capitalize",
  },
});
export const PriceInfoData = (props) => {
  const {
    baseColor,
    price_info,
    title,
    date,
    group_uid,
    remarks,
    pickup,
    address,
    adults,
    child,
  } = props;
  const { t } = useContext(TranslationContext);

  const classes = priceInfoDataStyles({ baseColor });

  var txt = `${adults} ${adults > 1 ? "Adults" : "Adult"}`;
  if (child) {
    txt += ` ${child} ${child > 1 ? "Children" : "Child"}`;
  }

  const cxls = _.get(price_info, "cancellation", []).filter(
    (cxl) => cxl.group_uid === group_uid
  );

  return (
    <Timeline className={classes.timeline}>
      <Timeline.Item dot={<Icon icon="map-marker" />}>
        <h3>{title}</h3>
      </Timeline.Item>
      <Timeline.Item dot={<Icon icon="group" />}>
        <h4>{txt}</h4>
      </Timeline.Item>
      <Timeline.Item dot={<Icon icon="calendar" />}>
        <h4>At {DateTime.fromISO(date).toLocaleString()}</h4>
      </Timeline.Item>
      <Timeline.Item dot={<Icon icon="money" />}>
        <h2 className={classes.price}>
          {price_info.price[0].value}
          {price_info.price[0].currency}
        </h2>
        <h3>
          <small>
            ({price_info.price[0].value_per_person}
            {price_info.price[0].currency}/{t("person")})
          </small>
        </h3>
      </Timeline.Item>
      <Timeline.Item dot={<Icon icon="close" />}>
        <h4>{t("cancellationPolicy")}</h4>
        {cxls.map((cxl, idx) => {
          const from = DateTime.fromISO(cxl.from);
          const to = DateTime.fromISO(cxl.to);

          return (
            <p className={classes.normalText} key={idx}>
              <span className={classes.capitalize}>{`${t("from")} `}</span>
              <strong>{from.toLocaleString()}</strong>
              {` ${t("to")} `}
              <strong>{to.toLocaleString()}</strong>
              {` ${t("cancelationIs")} `}
              <strong>{cxl.type}</strong>
            </p>
          );
        })}
      </Timeline.Item>
      {pickup.name ? (
        <Timeline.Item dot={<Icon icon="map-marker" />}>
          <h4>{t("pickup")}</h4>
          <p className={classes.normalText}>{pickup.name}</p>
          {address ? <p>{address}</p> : null}
        </Timeline.Item>
      ) : null}
      {remarks ? (
        <Timeline.Item dot={<Icon icon="comment" />}>
          <h4>{t("bookingRemarks")}</h4>
          <p className={classes.normalText}>{remarks}</p>
        </Timeline.Item>
      ) : null}
    </Timeline>
  );
};
PriceInfoData.defaultProps = {
  remarks: "",
  pickup: { name: "" },
  address: "",
};
PriceInfoData.propTypes = {
  baseColor: PropTypes.string.isRequired,
  adults: PropTypes.number.isRequired,
  child: PropTypes.number.isRequired,
  group_uid: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  price_info: PropTypes.object.isRequired,
  remarks: PropTypes.string.isRequired,
  pickup: PropTypes.object.isRequired,
  address: PropTypes.string.isRequired,
};

const mIPriceInfoStyles = createUseStyles({
  MIPriceInfo: (props) => ({
    "position": "fixed",
    "transform": "translateY(-50%)",
    "background": props.background,
    "top": "50%",
    "left": "10%",
    "width": "80%",
    "zIndex": 9998,
    "& .rs-panel-body": {
      "& .rs-timeline": {
        padding: `calc(${jssVariables.normal_gap} / 4)`,
        maxHeight: "70vh",
        overflow: "auto",
        marginLeft: `calc(${jssVariables.normal_gap} / 4)`,
      },
    },
  }),
  actions: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridGap: jssVariables.normal_gap,
    alignSelf: "end",
    marginTop: `calc(${jssVariables.normal_gap} * 2)`,
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    MIPriceInfo: (props) => ({
      width: "40%",
      left: "30%",
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    MIPriceInfo: (props) => ({
      width: "30%",
      left: "35%",
    }),
  },
});
const MIPriceInfo = (props) => {
  const {
    title,
    price_info,
    date,
    adults,
    child,
    onResetPrice,
    onInitBooking,
  } = props;

  const classes = mIPriceInfoStyles(props);
  const group_uid = _.get(price_info, "price[0].group_uid");
  const { t } = useContext(TranslationContext);

  return (
    <Panel className={classes.MIPriceInfo} shaded>
      <PriceInfoData
        baseColor={props.baseColor}
        adults={adults}
        title={title}
        child={child}
        date={date}
        price_info={price_info}
        group_uid={group_uid}
      />
      <div className={classes.actions}>
        <IconButton
          icon={<Icon icon="angle-left" />}
          appearance="ghost"
          onClick={onResetPrice}>
          {t("back")}
        </IconButton>
        <IconButton
          icon={<Icon icon="send" />}
          appearance="primary"
          placement="right"
          onClick={onInitBooking}>
          {t("bookNow")}
        </IconButton>
      </div>
    </Panel>
  );
};
MIPriceInfo.propTypes = {
  baseColor: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  price_info: PropTypes.object.isRequired,
  adults: PropTypes.number.isRequired,
  child: PropTypes.number.isRequired,
  date: PropTypes.string,
  onResetPrice: PropTypes.func.isRequired,
  onInitBooking: PropTypes.func.isRequired,
};
export default MIPriceInfo;
